import React from 'react';
import { CostByDimension, CostDimension } from '../../types/costExplorer';

/**
 * Dimension Breakdown Component
 * Displays cost breakdown by selected dimension with drill-down capability
 * AC 1: Multi-dimensional group-by with drill-down
 */
interface DimensionBreakdownProps {
  data: CostByDimension[];
  dimension: CostDimension | string;
  currency: string;
  onDrillDown?: (dimensionValue: string) => void;
}

export const DimensionBreakdown: React.FC<DimensionBreakdownProps> = ({
  data,
  dimension,
  currency,
  onDrillDown,
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getDimensionLabel = (dim: CostDimension | string): string => {
    // A custom cost-allocation-tag selection is passed as the literal string
    // "tag:<key>" (see CostExplorerPage's dimension dropdown), not a
    // CostDimension enum member - handle that before the static lookup below.
    if (dim.startsWith('tag:')) {
      return `Tag: ${dim.slice(4)}`;
    }
    const labels: Record<CostDimension, string> = {
      [CostDimension.CLOUD]: 'Cloud Provider',
      [CostDimension.ACCOUNT]: 'Account',
      [CostDimension.SERVICE]: 'Service',
      [CostDimension.REGION]: 'Region',
      [CostDimension.TAG]: 'Tag',
      [CostDimension.APPLICATION]: 'Application',
      [CostDimension.BUSINESS_UNIT]: 'Business Unit',
      [CostDimension.ENVIRONMENT]: 'Environment',
      [CostDimension.CUSTOM]: 'Resource',
    };
    return labels[dim as CostDimension] ?? 'Dimension';
  };

  const getTrendIcon = (direction: 'up' | 'down' | 'stable') => {
    if (direction === 'up') {
      return (
        <svg
          className="h-4 w-4 text-rose-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-label="Cost increased"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      );
    } else if (direction === 'down') {
      return (
        <svg
          className="h-4 w-4 text-emerald-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-label="Cost decreased"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      );
    }
    return (
      <svg
        className="h-4 w-4 text-muted-foreground/70"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-label="Cost stable"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
      </svg>
    );
  };

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          Cost by {getDimensionLabel(dimension)}
        </h3>
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <h3 className="mb-1 text-lg font-semibold text-foreground">
        Cost by {getDimensionLabel(dimension)}
      </h3>
      {dimension === CostDimension.CUSTOM && (
        // The backend caps the resource breakdown to the top 50 by cost for
        // every provider (see the LIMIT 50 in s3-data.service.ts's and
        // gcp-bigquery-data.service.ts's resource queries) - without this
        // note, a tenant with more than 50 distinct resources has no way to
        // know the list below is a top-N slice rather than everything.
        <p className="mb-3 text-xs text-muted-foreground">
          Showing the top 50 resources by cost.
        </p>
      )}

      <div className="space-y-3">
        {data.map((item, index) => (
          <div
            key={index}
            className={`rounded-lg border border-border p-4 transition-colors dark:border-border ${
              onDrillDown ? 'cursor-pointer hover:bg-muted/30 dark:hover:bg-secondary' : ''
            }`}
            onClick={() => onDrillDown?.(item.dimensionValue)}
            onKeyDown={(e) => {
              if (onDrillDown && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onDrillDown(item.dimensionValue);
              }
            }}
            role={onDrillDown ? 'button' : undefined}
            tabIndex={onDrillDown ? 0 : undefined}
            aria-label={
              onDrillDown
                ? `Drill down into ${item.dimensionValue} costs`
                : `${item.dimensionValue} costs`
            }
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className="min-w-0 break-all font-medium text-foreground"
                    title={item.dimensionValue}
                  >
                    {item.dimensionValue}
                  </span>
                  {item.trend && (
                    <div className="flex items-center gap-1">
                      {getTrendIcon(item.trend.direction)}
                      <span
                        className={`text-xs ${
                          item.trend.direction === 'up'
                            ? 'text-rose-600 dark:text-rose-400'
                            : item.trend.direction === 'down'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {Math.abs(item.trend.value).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {formatPercentage(item.percentage)} of total
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-foreground">
                  {formatCurrency(item.cost)}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all dark:bg-primary"
                style={{ width: `${item.percentage}%` }}
                role="progressbar"
                aria-valuenow={item.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${formatPercentage(item.percentage)} of total cost`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
